import {
  // EvereeEmbedExperience,
  Compensation,
  Performer,
  PerformerContractService,
  PerformerContractStatus,
  PerformerContractType,
  PerformerService,
  TContractSignatureRequest,
  TCreatePerformerRequest,
  THireExistingPerformerRequest,
  TUpdatePerformerRequest,
} from '@talon/web/lib/api';
import { OtherProp } from '@talon/web/lib/api/models/OtherProp';
import { PaymentInstructions } from '@talon/web/lib/api/models/PaymentInstructions';
import { TimeLog } from '@talon/web/lib/api/models/TimeLog';
import { WardrobeCounts } from '@talon/web/lib/api/models/WardrobeCounts';
import { action, makeObservable, observable, runInAction, toJS } from 'mobx';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually

interface ContractFormValues {
  projectId: string;
  performerId: string;
  contractType: PerformerContractType;
  signature?: string;
  status?: PerformerContractStatus;
  pdfUrl?: string;

  // Contract date fields (matching actual model)
  contractDayAndMonth?: string;
  contractTwoDigitYear?: string;

  // Producer and agency info
  producerName?: string;
  agencyName?: string;
  agencyAddress?: string;
  producerAddress?: string;

  // Advertiser and product (combined field in model)
  advertiserAndProduct?: string;

  // Commercial details
  commercialTitlesAndAdIds?: string;
  numberOfCommercials?: number;
  numberOfTags?: number;

  // Role and performance details
  classification?: string;
  partToBePlayed?: string;
  cityAndState?: string;
  placeOfEngagement?: string;
  dateAndHoursOfEmployment?: string;

  // Compensation
  compensation?: Compensation[];

  // Performance type boolean flags
  isOnCamera?: boolean;
  isOffCamera?: boolean;
  isPrincipalPerformer?: boolean;
  isStuntPerformer?: boolean;
  isSpecialtyAct?: boolean;
  isPilot?: boolean;
  isStuntCoordinator?: boolean;
  isDancerSoloOrDuo?: boolean;
  isDancer3Plus?: boolean;
  isSingerSoloOrDuo?: boolean;
  isGroupSinger3To5?: boolean;
  isGroupSinger6To8?: boolean;
  isGroupSinger9Plus?: boolean;
  isContractor?: boolean;

  // Usage type boolean flags
  isSeasonal?: boolean;
  isTestOrTestMarket?: boolean;
  isNonAir?: boolean;
  isTraditionalDigital?: boolean;
  isTheatricalIndustrial?: boolean;
  isForeignLanguageTranslation?: boolean;
  isMultipleTrackingOrSweetening?: boolean;

  // Payment flags
  isPaymentToPerformer?: boolean;
  isPaymentToAgent?: boolean;
  isJpcAuthorizerAgencyOrAdvertiser?: boolean;

  // Wardrobe
  isWardrobeFurnishedByPerformer?: boolean;
  isWardrobeFurnishedByProducer?: boolean;
  wardrobeCounts?: WardrobeCounts;
  wardrobeFeeTotal?: string;

  // Additional compensation and adjustments
  hazardAdjustment?: string;
  travelZoneAllowance?: number;
  tolls?: string;
  mileage?: string;
  parking?: string;
  vehicleType?: string;
  flightInsuranceOptIn?: boolean;
  nightPremium?: boolean;

  // Employer and legal
  employerOfRecordNameAndAddress?: string;
  specialProvisions?: string;

  // Props and adjustments
  propsUsed?: string[];
  otherProp?: OtherProp;
  adjustments?: string[];

  // Guardian info
  parentOrGuardian?: string;

  // Payment and notices
  paymentInstructions?: PaymentInstructions;

  // Time logs
  timeLogs?: TimeLog[];
}

interface TimeLogFormValues {
  date: string;
  workFrom?: string;
  workTo?: string;
  mealsFrom?: string;
  mealsTo?: string;
  travelToLocationFrom?: string;
  travelToLocationTo?: string;
  travelFromLocationFrom?: string;
  travelFromLocationTo?: string;
  fittingsMakeupTestFrom?: string;
  fittingsMakeupTestTo?: string;
}

interface ArrivalFormValues {
  arrivalTime: string;
  notes?: string;
  startTimeTracking: boolean;
}

interface EmailFormValues {
  to: string;
  cc?: string;
  subject: string;
  message?: string;
}

export class PerformerStore extends BaseStore {
  _performers: Performer[];
  _loading: boolean;
  _error: string | null;
  isSaving = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
    this._performers = [];
    this._loading = false;
    this._error = null;

    makeObservable(this, {
      _performers: observable,
      _loading: observable,
      _error: observable,
      isSaving: observable,
      setLoading: action,
      setError: action,
      setPerformers: action,
      loadPerformersByProject: action,
      createPerformer: action,
      updatePerformer: action,
      deletePerformer: action,
      // createContract: action,
      updateContract: action,
      declineContract: action,
      getContract: action,
      sendContract: action,
      logTime: action,
      deleteTimeLog: action,
      approveTimeLog: action,
      // markArrival: action,
    });
  }

  setIsSaving(isSaving: boolean) {
    runInAction(() => (this.isSaving = isSaving));
  }

  setLoading = (loading: boolean) => {
    this._loading = loading;
  };

  setError = (error: string | null) => {
    this._error = error;
  };

  setPerformers = (performers: Performer[]) => {
    this._performers = performers;
  };

  get performers() {
    return toJS(this._performers);
  }

  get loading() {
    return this._loading;
  }

  get error() {
    return this._error;
  }

  /**
   * Performers
   */

  createPerformer = async ({ projectId, ...req }: TCreatePerformerRequest) => {
    try {
      this.setLoading(true);
      this.setIsSaving(true);
      const { performer } = await PerformerService.createPerformer({ projectId, ...req });
      // If creating a performer for the current project, add it to the performers list
      if (
        this._performers.length &&
        this._performers[0]?.contracts.find((contract) => contract.project.id === projectId)
      ) {
        runInAction(() => {
          this._performers.push(performer);
        });
      }
      return performer;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
      this.setIsSaving(false);
    }
  };

  /**
   * Hire existing performer
   * @param performerId
   * @param projectId
   * @returns
   */
  hireExistingPerformer = async (req: THireExistingPerformerRequest) => {
    try {
      this.setLoading(true);
      this.setIsSaving(true);
      const { performer } = await PerformerService.hireExistingPerformer(req);
      // If hiring a performer for the current project, add it to the performers list
      if (
        this._performers.length &&
        this._performers[0]?.contracts.find((contract) => contract.project.id === req.projectId)
      ) {
        runInAction(() => {
          //update this performer in the list
          const index = this._performers.findIndex((p) => p.id === performer.id);
          if (index !== -1) {
            this._performers[index] = performer;
          } else {
            this._performers.push(performer);
          }
        });
      }
      return performer;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
      this.setIsSaving(false);
    }
  };

  resendInvite = async (performer: Performer, projectId: string) => {
    if (!performer || !projectId) {
      this.handleError('Performer data or Project ID missing. Cannot invite performer.');
      return;
    }
    try {
      this.setLoading(true);
      await PerformerService.invitePerformer({
        performerId: performer.id,
        email: performer.email,
        projectId,
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
    }
  };

  updatePerformer = async (performer: Partial<Performer>) => {
    if (!performer.id) {
      throw new Error('Performer ID is required for update');
    }
    const updateRequest: TUpdatePerformerRequest = {
      ...performer,
      roleIds: performer.roles?.map((role) => role.id),
      lastName: performer.last_name ? performer.last_name : undefined,
      firstName: performer.first_name ? performer.first_name : undefined,
    };
    try {
      this.setLoading(true);
      this.setIsSaving(true);
      const { performer: updatedPerformer } = await PerformerService.updatePerformer(performer.id, updateRequest);

      // Update the performer in the list if it exists
      runInAction(() => {
        const index = this._performers.findIndex((p) => p.id === performer.id);
        if (index !== -1 && updatedPerformer) {
          this._performers[index] = updatedPerformer;
        }
      });

      return updatedPerformer;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
      this.setIsSaving(false);
    }
  };

  deletePerformer = async (performerId: string) => {
    try {
      this.setLoading(true);
      this.setIsSaving(true);
      await PerformerService.deletePerformer(performerId);

      // Remove the performer from the list
      runInAction(() => {
        this._performers = this._performers.filter((p) => p.id !== performerId);
      });

      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    } finally {
      this.setLoading(false);
      this.setIsSaving(false);
    }
  };

  getPerformer = async (performerId: string, relations?: string) => {
    try {
      this.setLoading(true);
      const { performer } = await PerformerService.getPerformer(performerId, relations);
      return performer;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
    }
  };

  lookupByEmail = async (email: string) => {
    try {
      this.setLoading(true);
      const { performer } = await PerformerService.lookupPerformerByEmail(email);
      return performer;
    } catch (e) {
      this.handleError(e);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  searchPerformers = async (search: string, projectId?: string) => {
    try {
      const { performers } = await PerformerService.listPerformers(projectId, search);
      return performers;
    } catch (e) {
      this.handleError(e);
      return [];
    }
  };

  loadPerformersByProject = async (projectId: string) => {
    try {
      this.setLoading(true);
      this.setError(null);
      const { performers } = await PerformerService.listPerformers(
        projectId,
        undefined,
        undefined,
        undefined,
        'contracts,roles'
      );

      runInAction(() => {
        this._performers = performers || [];
      });

      return performers;
    } catch (e) {
      this.handleError(e);
      this.setError(e instanceof Error ? e.message : 'Failed to load performers');
      return [];
    } finally {
      this.setLoading(false);
    }
  };

  uploadSelfie = async (selfie: File) => {
    try {
      this.setLoading(true);
      await PerformerService.uploadPerformerSelfie({ selfie });
    } catch (e) {
      this.handleError(e);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  getSelfieUrl = async (performerId: string) => {
    try {
      this.setLoading(true);
      const { url } = await PerformerService.getPerformerSelfieUrl(performerId);
      return url;
    } catch (e) {
      this.handleError(e);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  /**
	 * Create the everee embed session for onboarding
	//  */
  // createEvereeEmbedSession = async (experience: EvereeEmbedExperience) => {
  //   try {
  //     this.setLoading(true);
  //     const response = await PerformerService.createEmbedSession({experience});
  //     return response;
  //   } catch (e) {
  //     this.handleError(e);
  //   } finally {
  //     this.setLoading(false);
  //   }
  // };

  /**
   * Validate Onboarding Token
   */

  validateOnboardingToken = async (token: string) => {
    try {
      this.setLoading(true);
      const response = await PerformerService.validatePerformerOnboardingToken(token);
      return response;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * Contract Management
   */

  listPerformerContracts = async ({
    projectId,
    performerId,
    commercialId,
    relations,
  }: {
    projectId?: string;
    performerId?: string;
    commercialId?: string;
    relations?: string;
  }) => {
    if (!(projectId || performerId || commercialId)) {
      return [];
    }
    try {
      this.setLoading(true);
      this.setError(null);
      const { contracts } = await PerformerContractService.listPerformerContracts(
        performerId,
        commercialId,
        projectId,
        undefined,
        undefined,
        undefined,
        relations
      );
      return contracts;
    } catch (e) {
      this.handleError(e);
      this.setError(e instanceof Error ? e.message : 'Failed to load contracts');
      return [];
    } finally {
      this.setLoading(false);
    }
  };

  updateUsageGuaranteeAmount = async (contractId: string, usageGuaranteeAmount: number) => {
    try {
      const { contract } = await PerformerContractService.updatePerformerContract(contractId, {
        usage_guarantee_amount: usageGuaranteeAmount,
      });
      return contract;
    } catch (e) {
      this.handleError(e);
    }
  };

  cancelContract = async (contractId: string) => {
    try {
      this.setLoading(true);
      const { contract } = await PerformerContractService.cancelContract(contractId);
      return contract;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
    }
  };

  updateContract = async (contractId: string, contractData: ContractFormValues) => {
    try {
      this.setLoading(true);
      this.setIsSaving(true);

      // Type cast to any since the TUpdatePerformerContractRequest type seems outdated
      // and doesn't include all the fields that the actual API accepts
      const response = await PerformerContractService.updatePerformerContract(contractId, {
        // Contract date fields (matching actual model)
        contractDayAndMonth: contractData.contractDayAndMonth,
        contractTwoDigitYear: contractData.contractTwoDigitYear,

        // Producer and agency info
        producerName: contractData.producerName,
        agencyName: contractData.agencyName,
        agencyAddress: contractData.agencyAddress,
        producerAddress: contractData.producerAddress,

        // Advertiser and product (combined field)
        advertiserAndProduct: contractData.advertiserAndProduct,

        // Commercial details
        commercialTitlesAndAdIds: contractData.commercialTitlesAndAdIds,
        numberOfCommercials: contractData.numberOfCommercials,
        numberOfTags: contractData.numberOfTags,

        // Role and performance details
        classification: contractData.classification,
        partToBePlayed: contractData.partToBePlayed,
        cityAndState: contractData.cityAndState,
        placeOfEngagement: contractData.placeOfEngagement,
        dateAndHoursOfEmployment: contractData.dateAndHoursOfEmployment,

        // Compensation
        compensation: contractData.compensation,

        // Performance type boolean flags
        isOnCamera: contractData.isOnCamera,
        isOffCamera: contractData.isOffCamera,
        isPrincipalPerformer: contractData.isPrincipalPerformer,
        isStuntPerformer: contractData.isStuntPerformer,
        isSpecialtyAct: contractData.isSpecialtyAct,
        isPilot: contractData.isPilot,
        isStuntCoordinator: contractData.isStuntCoordinator,
        isDancerSoloOrDuo: contractData.isDancerSoloOrDuo,
        isDancer3Plus: contractData.isDancer3Plus,
        isSingerSoloOrDuo: contractData.isSingerSoloOrDuo,
        isGroupSinger3To5: contractData.isGroupSinger3To5,
        isGroupSinger6To8: contractData.isGroupSinger6To8,
        isGroupSinger9Plus: contractData.isGroupSinger9Plus,
        isContractor: contractData.isContractor,

        // Usage type boolean flags
        isSeasonal: contractData.isSeasonal,
        isTestOrTestMarket: contractData.isTestOrTestMarket,
        isNonAir: contractData.isNonAir,
        isTraditionalDigital: contractData.isTraditionalDigital,
        isTheatricalIndustrial: contractData.isTheatricalIndustrial,
        isForeignLanguageTranslation: contractData.isForeignLanguageTranslation,
        isMultipleTrackingOrSweetening: contractData.isMultipleTrackingOrSweetening,

        // Payment flags
        isPaymentToPerformer: contractData.isPaymentToPerformer,
        isPaymentToAgent: contractData.isPaymentToAgent,
        isJpcAuthorizerAgencyOrAdvertiser: contractData.isJpcAuthorizerAgencyOrAdvertiser,

        // Wardrobe
        isWardrobeFurnishedByPerformer: contractData.isWardrobeFurnishedByPerformer,
        isWardrobeFurnishedByProducer: contractData.isWardrobeFurnishedByProducer,
        wardrobeCounts: contractData.wardrobeCounts,
        wardrobeFeeTotal: contractData.wardrobeFeeTotal,

        // Additional compensation and adjustments
        hazardAdjustment: contractData.hazardAdjustment,
        travelZoneAllowance: contractData.travelZoneAllowance,
        tolls: contractData.tolls,
        mileage: contractData.mileage,
        parking: contractData.parking,
        vehicleType: contractData.vehicleType,
        flightInsuranceOptIn: contractData.flightInsuranceOptIn,
        nightPremium: contractData.nightPremium,

        // Employer and legal
        employerOfRecordNameAndAddress: contractData.employerOfRecordNameAndAddress,
        specialProvisions: contractData.specialProvisions,

        // Props and adjustments
        propsUsed: contractData.propsUsed,
        otherProp: contractData.otherProp,
        adjustments: contractData.adjustments,

        // Guardian info
        parentOrGuardian: contractData.parentOrGuardian,

        // Payment and notices
        paymentInstructions: contractData.paymentInstructions,

        // Time logs
        timeLogs: contractData.timeLogs,

        // Contract status fields
        signature: contractData.signature,
        status: contractData.status,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);

      return response.contract;
    } catch (e) {
      this.handleError(e);
      return undefined;
    } finally {
      this.setLoading(false);
      this.setIsSaving(false);
    }
  };

  declineContract = async (contractId: string, declineReason: string) => {
    try {
      this.setLoading(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await PerformerContractService.updatePerformerContract(contractId, { declineReason } as any);
      return response.contract;
    } catch (e) {
      this.handleError(e);
      return undefined;
    } finally {
      this.setLoading(false);
    }
  };

  uploadCharacterPhotos = async (contractId: string, characterPhotos: File[]) => {
    try {
      this.setLoading(true);
      this.setIsSaving(true);
      const response = await PerformerContractService.updateCharacterPhotos(contractId, { characterPhotos });
      return response.contract;
    } catch (e) {
      this.handleError(e);
      return null;
    } finally {
      this.setLoading(false);
      this.setIsSaving(false);
    }
  };

  /**
   * Generate a short-lived QR capture token for a contract.
   * Invalidates any previously issued token for this producer.
   */
  generateCaptureToken = async (contractId: string): Promise<string | null> => {
    try {
      this.setLoading(true);
      const response = await PerformerContractService.generateCaptureToken(contractId);
      return response.token;
    } catch (e) {
      this.handleError(e);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * Validate a capture token and return contract/performer info.
   * Used by the mobile capture page on load to confirm the link is still valid.
   */
  validateCaptureToken = async (token: string): Promise<{ contractId: string; performerName: string } | null> => {
    try {
      this.setLoading(true);
      return await PerformerContractService.validateCaptureToken(token);
    } catch (e) {
      this.handleError(e);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  getCharacterPhotoUrls = async (contractId: string) => {
    try {
      this.setLoading(true);
      const response = await PerformerContractService.getCharacterPhotos(contractId);
      return response;
    } catch (e) {
      this.handleError(e);
      return [];
    } finally {
      this.setLoading(false);
    }
  };

  downloadContractPdf = async (contractId: string) => {
    try {
      this.setLoading(true);
      const response = await PerformerContractService.downloadContractPdf(contractId);
      if (response) {
        // convert response from base64 string to blob
        const byteArray = Uint8Array.from(Buffer.from(response, 'base64'));
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // const url = window.URL.createObjectURL(blob);
        // const a = document.createElement('a');
        // a.href = url;
        // a.target = '_blank';
        // document.body.appendChild(a);
        // a.click();
        // a.remove();
        // window.URL.revokeObjectURL(url);
        return blob;
      }
      // return response;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
    }
  };

  getContract = async (contractId: string, relations?: string) => {
    try {
      this.setLoading(true);
      const response = await PerformerContractService.getPerformerContract(contractId, relations);
      return response.contract;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setLoading(false);
    }
  };

  sendContract = async (contractId: string, emailData: EmailFormValues) => {
    try {
      this.setLoading(true);
      await PerformerContractService.sendContractEmail(contractId, {
        email: emailData.to,
        message: emailData.message,
      });
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    } finally {
      this.setLoading(false);
    }
  };

  signContract = async (
    contractId: string,
    request: {
      field: TContractSignatureRequest.field;
      name: string;
      base64signatureImage: string;
      consentToESign: boolean;
      user_id: string;
      signedDate: string;
    }
  ) => {
    try {
      this.setLoading(true);
      const { contract } = await PerformerContractService.signContract(contractId, request);
      return contract;
    } catch (e) {
      this.handleError(e);
      return undefined;
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * Time Tracking
   */

  logTime = async (contractId: string, performerId: string, timeData: TimeLogFormValues) => {
    try {
      this.setLoading(true);
      await PerformerContractService.addTimeLog(contractId, {
        date: timeData.date,
        workFrom: timeData.workFrom,
        workTo: timeData.workTo,
        mealsFrom: timeData.mealsFrom,
        mealsTo: timeData.mealsTo,
        travelToLocationFrom: timeData.travelToLocationFrom,
        travelToLocationTo: timeData.travelToLocationTo,
        travelFromLocationFrom: timeData.travelFromLocationFrom,
        travelFromLocationTo: timeData.travelFromLocationTo,
        fittingsMakeupTestFrom: timeData.fittingsMakeupTestFrom,
        fittingsMakeupTestTo: timeData.fittingsMakeupTestTo,
      });
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    } finally {
      this.setLoading(false);
    }
  };

  deleteTimeLog = async (contractId: string, timeLogId: string) => {
    try {
      this.setLoading(true);
      await PerformerContractService.deleteTimeLog(contractId, timeLogId);
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    } finally {
      this.setLoading(false);
    }
  };

  approveTimeLog = async (contractId: string, initials: string) => {
    try {
      this.setLoading(true);
      const response = await PerformerContractService.approveTimeLog(contractId, { initials });
      return response.contract;
    } catch (e) {
      this.handleError(e);
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  markArrival = async (_contractId: string, _performerId: string, _arrivalData: ArrivalFormValues) => {
    try {
      this.setLoading(true);
      // await PerformerContractService.markArrival(contractId, {
      //   performerId,
      //   arrivalTime: arrivalData.arrivalTime,
      //   notes: arrivalData.notes,
      //   startTimeTracking: arrivalData.startTimeTracking,
      // });
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * Helper methods
   */

  private refreshPerformerInfo = async (performerId: string) => {
    try {
      const performer = await this.getPerformer(performerId);
      if (performer) {
        runInAction(() => {
          const index = this._performers.findIndex((p) => p.id === performerId);
          if (index !== -1) {
            this._performers[index] = performer;
          }
        });
      }
    } catch (e) {
      console.error('Failed to refresh performer info:', e);
    }
  };
}
